/**
 * AgentLayout — ShellLayout thin wrapper (chat 전용).
 *
 * 공통 셸(ShellLayout) + AgentLayoutContext + MobileTabBar.
 * 헤더는 ChatZone이 자체 ChatHeader로 렌더 (renderHeader=false).
 */
import {
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { ShellLayout } from "@/shared/layouts/shell-layout";
import { MobileTabBar } from "@/agent/ui/chat/mobile-tab-bar";
import { useLayoutMode } from "@/agent/state/use-layout-mode";
import type { LayoutMode } from "@modootoday/core-agent/types";
import { usePersona } from "@/agent/state/use-persona";
import { getPersonaLayoutMode } from "@modootoday/core-agent/persona";

/* ═══ Context ═══ */

export interface AgentLayoutContextValue {
  mode: LayoutMode;
  setMode: (mode: LayoutMode) => void;
  goAgentFirst: () => void;
  goDetailFirst: (path?: string) => void;
  goSplitView: () => void;
  detailPath: string | null;
  showArtifact: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
}

const AgentLayoutContext = createContext<AgentLayoutContextValue | null>(null);

export function useAgentLayout(): AgentLayoutContextValue {
  const ctx = useContext(AgentLayoutContext);
  if (!ctx) throw new Error("useAgentLayout must be used within AgentLayout");
  return ctx;
}

/* ═══ AgentLayout ═══ */

export interface AgentLayoutProps {
  children?: React.ReactNode;
  initialMode?: LayoutMode;
  className?: string;
}

export function AgentLayout({
  children,
  initialMode = "agent-first",
  className,
}: AgentLayoutProps) {
  const layoutMode = useLayoutMode(initialMode);
  const navigate = useNavigate();
  const { persona, isLoaded: personaLoaded } = usePersona();

  useEffect(() => {
    if (!personaLoaded) return;
    if (persona && initialMode === "agent-first") {
      const personaMode = getPersonaLayoutMode(
        persona.id,
        typeof window !== "undefined" ? window.innerWidth : 1280,
      );
      if (personaMode !== "agent-first") {
        layoutMode.setMode(personaMode);
      }
    }
  }, [personaLoaded, persona?.id]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeRailItem, setActiveRailItem] = useState<string>("chat");

  const handleRailItemClick = useCallback(
    (id: string) => {
      setActiveRailItem(id);
      if (id === "chat") {
        layoutMode.goAgentFirst();
        setHistoryOpen(false);
      } else if (id === "dashboard") {
        void navigate({ to: "/dashboard" });
      }
    },
    [layoutMode, navigate],
  );

  const ctxValue: AgentLayoutContextValue = {
    ...layoutMode,
    historyOpen,
    setHistoryOpen,
  };

  const { isMobile } = layoutMode;

  return (
    <AgentLayoutContext.Provider value={ctxValue}>
      <ShellLayout
        railActiveItem={activeRailItem}
        onRailItemClick={handleRailItemClick}
        renderHeader={false}
        mobileNav={
          isMobile ? (
            <MobileTabBar
              activeTab={activeRailItem}
              onTabChange={handleRailItemClick}
            />
          ) : undefined
        }
        className={className}
      >
        {children ?? <Outlet />}
      </ShellLayout>
    </AgentLayoutContext.Provider>
  );
}
